import { DOCUMENT } from '@angular/common';
import { AfterViewInit, Directive, Inject, OnDestroy, OnInit, inject } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';



import { Fai, ProductFactory, QuoteModel, Sowo } from '@bytel/bytel-sales';
import { UserService } from '@bytel/checkout';



import { AppService } from '@services/app.services';
import { EventsService } from '@services/events.service';
import { MainCartService } from '@services/main-cart.service';
import { MessageService } from '@services/message.service';
import { CheckoutOauth2ResourcesService } from '@services/oauth2-ressources.service';
import { RecallService } from '@services/recall.service';
import { StepperService } from '@services/stepper.service';
import { TagGeneratorServices } from '@services/tag-generator.services';



import { BasicObject } from '@interfaces/checkout.interface';



import { Step } from '@shared/stepper/steps/step';
import { TagService } from '@shared/tag/tag.service';



import { Observable, ReplaySubject, Subject, Subscription, of } from 'rxjs';
import { mergeMap, takeUntil } from 'rxjs/operators';
import { SwappyModalComponent } from '../shared/swappy-modal/swappy-modal.component';


@Directive()
export abstract class TunnelComponent implements OnInit, OnDestroy, AfterViewInit {
    public isSowo: boolean = false;
    public additionnalTags: BasicObject = {};
    public isSubmitted: boolean;
    public nextSteps: Step[] = [];
    public previousSteps: Step[] = [];
    public step: Step;
    public validationSubscription: Subscription;
    public isLoading: boolean = false;
    public form: FormGroup;
    public isPreorder: boolean = false;
    public skeletonSubscription: ReplaySubject<boolean>;
    public title: string = '';
    public isDebugActive: boolean = false;

    @Inject(DOCUMENT) protected dom: Document = inject(DOCUMENT);
    protected route: ActivatedRoute = inject(ActivatedRoute);
    protected SERIE_SPECIAL_XGBOX = 'ss_medium_xgbox';
    protected destroy$ = new Subject<void>();

    protected recallService: RecallService = inject(RecallService);

    public abstract page: string;

    public constructor(
        // NOSONAR
        protected stepperService: StepperService,
        protected messageService: MessageService,
        protected cartService: MainCartService,
        protected checkoutTagsServices: TagGeneratorServices,
        protected tagService: TagService,
        protected appService: AppService,
        protected userService: UserService,
        protected oauth2Resource: CheckoutOauth2ResourcesService,
        protected eventsService: EventsService,
    ) {
        this.validationSubscription = this.stepperService.validateSubject.pipe(takeUntil(this.destroy$)).subscribe({
            next: () => {
                this.validateStep();
            },
        });

        this.step = this.stepperService.currentStep;
        this.skeletonSubscription = this.appService.resolversSubject;
        this.isDebugActive = this.appService.debugMode.active;
    }

    public initTags(): Observable<boolean> {
        return of(true);
    }

    public sendTags(): void {
        this.initTags().subscribe(() => {
            this.setAdditionnalTags();
            const tags = this.checkoutTagsServices.getTags(
                this.page,
                this.stepperService.currentStep.ecommerceStep,
                this.additionnalTags,
            );
            this.tagService.currentCheckoutTags = tags;
            this.tagService.send(tags);
        });
    }

    public ngOnInit(doNotHandleTagsSending: boolean = false): void {
        const quote: QuoteModel = this.cartService.getQuote();
        this.cartService.paymentMethodObs.subscribe(() => {
            if (this.cartService.isSwappyCart()) {
                // Handle swappy cart
                const cart = this.cartService.cartModel;
                if (!cart.fundingMethod) {
                    this.messageService.openModal(SwappyModalComponent, {
                        hasSwappyError: true,
                        title: 'Votre commande ne peut être finalisée en ligne',
                    });
                }
            }
        });
        this.cartService.cartQuoteSubscription.subscribe(() => {
            this.isSowo = ProductFactory.Is(quote.getProductByType(Sowo), Sowo, true);
            this.title = '';
            if (this._checkIfXgbox26(quote)) {
                this.title = 'Offre -26 ans';
            }
        });

        this.nextSteps = this.stepperService.getNextStepsReminders();
        this.appService.formStatusSubject.next({ ...this.appService.formStatusSubject.getValue(), invalid: false });
        this.isPreorder = quote.isPreorder();

        this.initStep();
        this.previousSteps = this.stepperService.getPreviousStepsReminders();
        this.appService.debugSubject.subscribe((debug) => (this.isDebugActive = debug.active));
        this.setDynamicRecallParam();

        if (!doNotHandleTagsSending) {
            this.sendTags();
        }
    }

    public ngAfterViewInit(): void {
        if (this.form) {
            this.appService.subscribeToFormStatusChanges(this.form);
        }
        this.appService.hideSkeleton();

        this.eventsService.launch('stepStart', { step: this.step.key });
    }

    public initStep(): void {
        // default
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.validationSubscription.unsubscribe();
        this.appService.unsubscribeFromFormStatusChanges();
        this.messageService.resetErrors();
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public setAdditionnalTags(params: BasicObject = {}): void {
        this.additionnalTags = {};
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public validate(params: BasicObject = null): Observable<boolean> {
        return of(true);
    }

    public validateStep(params: BasicObject = null): void {
        if (!this.isLoading) {
            this.isLoading = true;
            this.appService.formStatusSubject.next({ invalid: false, loading: true });
            this.validate(params)
                .pipe(
                    mergeMap((validated: boolean) => {
                        if (!validated) {
                            this.stepperService.triggerValidationErrors();
                            return of(false);
                        }

                        return this.stepperService.initNextStep();
                    }),
                )
                .subscribe({
                    next: (validated: boolean) => {
                        this.isLoading = false;
                        this.appService.formStatusSubject.next({
                            ...this.appService.formStatusSubject.getValue(),
                            loading: false,
                        });
                        if (validated) {
                            this.stepperService.goToNextStep();
                        }
                    },
                    error: (err) => {
                        this.isLoading = false;
                        this.appService.formStatusSubject.next({
                            ...this.appService.formStatusSubject.getValue(),
                            loading: false,
                        });
                        this.messageService.addMessage(err, true);
                        this.stepperService.triggerValidationErrors();
                    },
                });
        }
    }

    protected setDynamicRecallParam(): void {
        const recallId = this.cartService.getQuote().isAcquisitionFix() ? 'tlvctcfai4' : 'tlvctcmobile3';
        this.recallService.setMetaRecall(recallId);
    }

    private _checkIfXgbox26(quote: QuoteModel): boolean {
        return quote.products.some(
            (product) => ProductFactory.Is(product, Fai) && product.data.commercial_series === this.SERIE_SPECIAL_XGBOX,
        );
    }
}
